<template>
    <AppModal v-model="modelValue" :close-button="false">
        <template #header>
            <h3 class="m-0">
                {{ $t('Add iCode Report') }}
                <template v-if="patient">
                    {{ $t('for Patient') }}
                    {{ patient.last_name }}, {{ patient.first_name }}
                </template>
            </h3>
        </template>

        <div v-if="!patient" class="alert alert-info">
            {{ $t('This is an anonymous patient iCode upload. iCodes submitted here are NOT saved to any patient record.') }}
        </div>

        <AddICodeForm :form="form" :disable-serial="!!serial" @submit.prevent="submitForm" />

        <div v-if="form.errors.codes" class="text-danger small">
            {{ form.errors.codes }}
        </div>

        <template #footer>
            <div v-if="hasErrors" class="text-danger small">
                {{ $t('Failed to process code. Check the data and try again') }}
            </div>

            <AppButton btn-class="btn-link" :is-disabled="form.processing" @click.prevent="clearForm">
                {{ $t('Close') }}
            </AppButton>
            <AppButton type="submit" form="add-icode-form" :is-loading="form.processing">
                {{ $t('Submit') }}
            </AppButton>
        </template>
    </AppModal>
</template>

<script setup lang="ts">
import { type PropType, ref } from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import { type AxiosResponse } from "axios";
import { useForm } from 'laravel-precognition-vue';
import { createConfirmDialog } from "vuejs-confirm-dialog";
import AppButton from "@/vue/shared/AppButton.vue";
import AppModal from "@/vue/shared/AppModal.vue";
import AddICodeForm from "@/vue/connect/icode/AddICodeForm.vue";
import AppConfirm from "@/vue/shared/dialog/AppConfirm.vue";
import { type Patient } from "@/vue/types/patient";
import { type ReportType } from "@/vue/types/report";
import { type AddICodeFormType } from "@/vue/types/forms/addICodeForm";

const modelValue = defineModel<boolean>({ required: true });
const props = defineProps({
    patient: {
        type: Object as PropType<Patient>,
        required: false,
    },
    serial: {
        type: String,
        required: false,
    },
});
const emits = defineEmits(['icode:created']);

const form = useForm<AddICodeFormType>('post', '/api/icode-reports', {
    patient_id: props.patient?.id || null,
    serial: props.serial || '',
    codes: [
        {
            collected: '',
            icode: '',
        },
    ],
}, {
    timeout: 3000,
});

const { reveal: alert } = createConfirmDialog(AppConfirm);

const clearForm = async () => {
    const confirmed = await alert({
        title: $t('Are you sure?')
    });
    if (confirmed.isCanceled) return;

    form.reset();
    hasErrors.value = false;
    modelValue.value = false;
};

const hasErrors = ref(false);
const submitForm = async () => {
    const confirmed = await alert({
        title: $t('Are you sure?')
    });
    if (confirmed.isCanceled) return;

    hasErrors.value = false;
    try {
        const { data } = await form.submit() as AxiosResponse<ReportType[]>;
        form.reset();
        emits('icode:created', data);
    } catch (e) {
        // mark if it is not a validation error
        if (!form.hasErrors) hasErrors.value = true;
    }
};
</script>
